import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema } from '../utils/structuredData';
import { LayoutDashboardIcon, SparklesIcon, ArrowRightIcon, CameraIcon, SearchIcon } from 'lucide-react';
export function ProductsPage() {
  usePageSEO({
    canonical: '/products',
    keywords: 'Shopify products, Shopify themes, Shopify apps, ecommerce tools, FlowBoost, AI Photoshoot',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Products',
      path: '/products'
    }]),
    title: 'Products | Design Flow Digitals',
    description: 'Discover premium Shopify themes, apps, and tools by Design Flow Digitals. FlowBoost speed optimization, AI Photoshoot, and custom-built ecommerce solutions.'
  });
  return <div className="pt-32 pb-20" data-id="element-1207">
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-24" data-id="element-1208">
        <h1 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-1209">Our Products</h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed" data-id="element-1210">
          Premium themes and smart apps built to accelerate your ecommerce
          growth.
        </p>
      </section>

      <div className="flex flex-col gap-16 max-w-6xl mx-auto px-6" data-id="element-1211">
        {/* Category Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1212">
          {/* Themes Category */}
          <div className="group glass-card p-10 border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 relative overflow-hidden flex flex-col h-full" data-id="element-1213">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-[80px] pointer-events-none" data-id="element-1214" />

            <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-8 border border-[#D4AF37]/20" data-id="element-1215">
              <LayoutDashboardIcon className="w-8 h-8 text-[#D4AF37]" data-id="element-1216" />
            </div>

            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1217">
              Shopify Themes
            </h2>
            <p className="text-gray-400 mb-8 leading-relaxed flex-grow" data-id="element-1218">
              Premium Shopify themes designed for modern ecommerce brands. Built
              with clean code and conversion in mind.
            </p>

            <Link to="/themes" className="inline-flex items-center gap-2 text-white font-semibold hover:text-[#D4AF37] transition-colors" data-id="element-1219">
              Explore Themes{' '}
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1220" />
            </Link>
          </div>

          {/* Apps Category */}
          <div className="group glass-card p-10 border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 relative overflow-hidden flex flex-col h-full" data-id="element-1221">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-[80px] pointer-events-none" data-id="element-1222" />

            <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-8 border border-[#D4AF37]/20" data-id="element-1223">
              <SparklesIcon className="w-8 h-8 text-[#D4AF37]" data-id="element-1224" />
            </div>

            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1225">Shopify Apps</h2>
            <p className="text-gray-400 mb-8 leading-relaxed flex-grow" data-id="element-1226">
              Powerful tools to help merchants grow faster, automate workflows,
              and increase average order value.
            </p>

            <Link to="/apps" className="inline-flex items-center gap-2 text-white font-semibold hover:text-[#D4AF37] transition-colors" data-id="element-1227">
              Explore Apps{' '}
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1228" />
            </Link>
          </div>
        </div>

        {/* Featured App Highlight */}
        <div className="mt-12" data-id="element-1229">
          <div className="flex items-center gap-4 mb-8" data-id="element-1230">
            <h3 className="text-2xl font-bold text-white" data-id="element-1231">Latest Release</h3>
            <div className="h-px bg-white/10 flex-1" data-id="element-1232" />
          </div>

          <div className="glass-card p-8 md:p-12 border-emerald-500/30 bg-gradient-to-br from-emerald-900/10 to-transparent relative overflow-hidden group" data-id="element-1233">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-[80px] pointer-events-none" data-id="element-1234" />

            <div className="flex flex-col md:flex-row gap-10 items-center relative z-10" data-id="element-1235">
              <div className="w-24 h-24 shrink-0 rounded-2xl bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30 shadow-[0_0_30px_rgba(16,185,129,0.2)]" data-id="element-1236">
                <SearchIcon className="w-12 h-12 text-emerald-400" data-id="element-1237" />
              </div>

              <div className="flex-1 text-center md:text-left" data-id="element-1238">
                <div className="flex flex-col md:flex-row items-center gap-3 mb-3" data-id="element-1239">
                  <h3 className="text-3xl font-bold text-white" data-id="element-1240">SEO Suite</h3>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider" data-id="element-1241">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-1242" />{' '}
                    Latest Release
                  </span>
                </div>

                <p className="text-xl text-gray-300 mb-6 leading-relaxed" data-id="element-1243">
                  The all-in-one SEO solution for Shopify. Automate your
                  technical SEO, optimize images, and dominate search rankings.
                </p>

                <Link to="/seo-suite" className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-white bg-emerald-600 hover:bg-emerald-500 transition-colors shadow-[0_0_20px_rgba(16,185,129,0.3)]" data-id="element-1244">
                  View App Details <ArrowRightIcon className="w-4 h-4" data-id="element-1245" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}